import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  Badge,
  Input,
  Modal,
  ModalFooter,
  Pagination,
  Select,
  getStatusBadgeVariant,
  EntityLink,
  PolicyNumber,
  collapsedPolicyNumberSource,
} from '@/components/common';
import { commissionService, getErrorMessage } from '@/services';
// The Ledger tab's insurer dropdowns list THIS BROKER'S OWN insurers, never the
// platform's. `/insurers/insurers/lookup/` is not broker-scoped — InsurerViewSet
// narrows by country for a country_admin and by nothing else — so a broker
// filtering by insurer there is offered every insurance company on the platform,
// most of which can never appear in their ledger.
//
// `useBrokerInsurers(brokerId)` is the switcher-aware hook: it passes the
// BrokerSwitcher's current selection to `/brokers/my-insurance-companies/`.
// `useBrokerInsuranceCompanies()`, which this used to call, takes a brokerId and
// IGNORES it, so the server fell back to pinning one broker off `user.tenant_id`
// — and for a multi-broker user that is not the broker they are looking at. The
// dropdown then offered insurers with no ledger rows behind them and withheld
// the ones that had. Passing the id closes that; a broker the caller does not
// belong to is refused by the server (403), never quietly answered.
import { useBrokerInsurers } from '@/hooks/useBrokerInsurers';
import { usePOSPsLookup } from '@/hooks/useBrokers';
import { useAuthStore } from '@/store/auth';
import { useCanState } from '@/hooks/useEmployeeRights';
import { ADMIN_ROLES } from '@/services/employeeRights';
import {
  useCommissionCalculations,
  useCommissionPayouts,
  useCommissionLedger,
  useRunningBalance,
  useBrokerRates,
  useApproveCalculation,
  useDisputeCalculation,
  useCalculateCommissions,
  useCommissionCalcRun,
  useMarkPayoutProcessing,
  useMarkPayoutCompleted,
} from '@/hooks/useCommissions';
import {
  useByInsurerReport,
  usePospOutstanding,
} from '@/hooks/useCommissionReports';
import { formatCurrency, formatDate, titleCase } from '@/utils';
import { InsurerLogo } from '@/components/common/InsurerLogo';
import type {
  BrokerContext,
  CommissionCalculationStatus,
  CommissionPayoutRecord,
} from '@/types';
// #280. The ledger's `clawback` entry type and its `note` live here while
// `types/index.ts` is frozen; see the module for what to fold back in.
import { entryTypeBadgeVariant } from './commissionLedgerTypes';
import { formatCalcRunProgress } from './calcRunSummary';
import { CommissionReconciliationTab } from './CommissionReconciliationTab';
import { InsurerSettlementsTab } from './InsurerSettlementsTab';
import { CommissionTotalCell } from './CommissionTotalCell';
import { PayoutLinesModal } from './PayoutLinesModal';
import type { CommissionPayoutWithPosp } from '@/services/commissions';
import { ReportsTab } from './ReportsTab';
import { defaultPayoutRange } from './payoutPeriod';

const dateInputClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * #249 — the Effective From / Effective To date filters on the Rates tab.
 *
 * The keys ARE the API's query params (apps/commissions/filters.py); they are
 * forwarded verbatim by commissionService.getBrokerRates, which drops empty
 * values, so an untouched filter never reaches the server.
 *
 * `effective_until_after` is not the mirror image of `effective_until_before`,
 * and that is on purpose: a rate whose Effective To is blank has no end date —
 * the Add Rate form's input is optional and the commission engine reads a blank
 * one as open-ended — so it IS still in force after any date, and it never ends
 * before one. Filtering that column with a plain range would quietly hide every
 * open-ended rate.
 */
type RateDateFilters = {
  effective_from_after: string;
  effective_from_before: string;
  effective_until_after: string;
  effective_until_before: string;
};

const emptyRateFilters: RateDateFilters = {
  effective_from_after: '',
  effective_from_before: '',
  effective_until_after: '',
  effective_until_before: '',
};

/**
 * #253 — the POSP pay-out payment modes.
 *
 * ONE list, feeding both the Complete Payout dropdown and the Mode column in the
 * payout history table, so the label a user picked is character-for-character the
 * label they are shown afterwards.
 *
 * The values mirror `CommissionPayout.PAYMENT_MODE_CHOICES`
 * (backend/apps/commissions/models.py) and are what `mark_completed` validates
 * against; the order is the model's order. "By Cash" is appended, never inserted —
 * the ticket requires the existing three to be unchanged.
 */
const PAYMENT_MODE_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'cash', label: 'By Cash' },
];

/** Label for a stored payment_mode. An unknown code (a mode the backend gained
 *  before this file did) falls back to a readable form instead of rendering blank. */
export function paymentModeLabel(mode: string | null | undefined): string {
  if (!mode) return '—';
  return PAYMENT_MODE_OPTIONS.find((o) => o.value === mode)?.label ?? titleCase(mode);
}

/** Placeholder for the Payment Reference input. Cash has no UTR/cheque number,
 *  so asking for one by name is how a user ends up typing something meaningless;
 *  the field itself stays required, because an unreferenced cash payout is an
 *  untraceable one. */
export function paymentReferencePlaceholder(mode: string): string {
  return mode === 'cash' ? 'Cash receipt / voucher no.' : 'UTR / cheque no. / UPI ref';
}

/**
 * The Ledger tab's "By POSP" grouping, filtered SERVER-side (B8).
 *
 * Replaces `useByPospReport`, whose `ReportFilters` has no `posp` key: it could
 * only ever ask for the whole book, so the tab filtered the rows it got back in
 * the browser and hid its own pagination control whenever the filter was set.
 * On a book with more agents than one page that made "filter by agent" search a
 * subset and offer no way to reach the rest.
 *
 * Both params are real query params on `/commissions/reports/by-posp/` now, so
 * the filter and the paging agree and the CSV export answers the same question.
 */
function useByPospLedgerReport(page: number, filters: { insurer?: string; posp?: string }) {
  return useQuery({
    queryKey: ['byPospReport', 'ledger', page, filters],
    queryFn: () => commissionService.getByPospReport(page, filters),
    placeholderData: (prev) => prev,
  });
}

export interface CreatePayoutBatchesInput {
  /** POSP only. Broker-self payout CREATION was removed (B9); the completion,
   *  listing and ledger paths still handle the `broker` rows already in flight. */
  entity_type: 'posp';
  /** One or more entities to pay. Each gets its OWN payout — see below. */
  entities: { id: string; label: string }[];
  start_date: string;
  end_date: string;
}

/**
 * Create a payout batch for each selected entity.
 *
 * **One payout per POSP, never a shared one.** `POST create_batch` takes a
 * single `entity_id` and writes a `CommissionPayout` with a single `posp` FK;
 * the POSP-facing queryset is `filter(posp=posp_profile)`, and the detail
 * serializer exposes `bank_details`. A "multi-POSP payout" modelled as one row
 * covering several POSPs would have no way to stay scoped to one of them — that
 * is the exact shape of the payout PII leak fixed in an earlier round. So
 * "select multiple POSPs" means N independent payouts, each visible only to its
 * own recipient.
 *
 * Calls are sequential, not `Promise.all`: `create_batch` sweeps approved
 * calculations and flips them to `paid`, so overlapping sweeps against the same
 * broker's rows are not worth the round-trip saved. N is however many POSPs a
 * human ticked.
 *
 * Per-entity failures are collected rather than thrown — the common one is the
 * 400 "No approved commission calculations found for the selected
 * broker/period", which must not discard the payouts that did succeed.
 */
function useCreatePayoutBatches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePayoutBatchesInput) => {
      const created: string[] = [];
      const failed: { label: string; message: string }[] = [];
      for (const entity of input.entities) {
        try {
          await commissionService.createPayoutBatch({
            entity_type: input.entity_type,
            entity_id: entity.id,
            start_date: input.start_date,
            end_date: input.end_date,
          });
          created.push(entity.label);
        } catch (e) {
          failed.push({ label: entity.label, message: getErrorMessage(e) });
        }
      }
      return { created, failed };
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['commissionPayouts'] });
      qc.invalidateQueries({ queryKey: ['pospOutstanding'] });
    },
    onSuccess: ({ created, failed }) => {
      if (created.length === 1 && failed.length === 0) {
        toast.success('Payout batch created');
      } else if (created.length > 0) {
        toast.success(`${created.length} payout batches created`);
      }
      // Report every failure by name — "3 of 5 failed" with no names leaves the
      // user re-running the whole batch to find out which.
      for (const f of failed.slice(0, 3)) {
        toast.error(`${f.label}: ${f.message}`);
      }
      if (failed.length > 3) {
        toast.error(`…and ${failed.length - 3} more could not be paid out.`);
      }
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

type TabId = 'rates' | 'calculations' | 'comparison' | 'settlements' | 'ledger' | 'payouts' | 'reports';
type LedgerGrouping = 'chronological' | 'by-insurer' | 'by-posp';

// "Commission Reconciliation" sits immediately after Calculations on purpose:
// it is the same figures seen from the insurer's side (what was actually
// received vs what Calculations auto-approved), so the two are read together.
// It was briefly labelled "Commission Comparison"; renamed back to
// "Commission Reconciliation" because that is what it does — match the
// insurer's statement against the internally calculated commission — and
// "reconciliation" is the term brokers already use for that task.
const tabs: { id: TabId; label: string }[] = [
  { id: 'rates', label: 'Rates' },
  { id: 'calculations', label: 'Calculations' },
  { id: 'comparison', label: 'Commission Reconciliation' },
  // #444: what insurers say they paid, answerable by the broker.
  { id: 'settlements', label: 'Insurer Settlements' },
  { id: 'ledger', label: 'Ledger' },
  { id: 'payouts', label: 'Payouts' },
  { id: 'reports', label: 'Reports' },
];

const calcStatusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'calculated', label: 'Calculated' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'disputed', label: 'Disputed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function CommissionsListPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('calculations');

  // Per-tab pagination
  const [ratesPage, setRatesPage] = useState(1);
  const [calcsPage, setCalcsPage] = useState(1);
  const [payoutsPage, setPayoutsPage] = useState(1);

  // Calculations filter
  const [calcStatusFilter, setCalcStatusFilter] = useState('');

  // Rates filter (#249) — the Effective From / Effective To date filters.
  // Sent straight through as query params; the backend FilterSet
  // (apps/commissions/filters.py) is what gives them meaning, including the
  // one thing a naive range gets wrong: a rate with a blank Effective To has
  // no end date, so it still counts as in force after any date.
  const [rateFilters, setRateFilters] = useState<RateDateFilters>(emptyRateFilters);

  const user = useAuthStore((s) => s.user);
  const loginType = useAuthStore((s) => s.loginType);
  const tenantContext = useAuthStore((s) => s.tenantContext);
  // "Myself" on the Create Payout modal (and the running balance) must mean the
  // broker the user is CURRENTLY working in — i.e. whatever BrokerSwitcher last
  // selected — not user.tenant_id. A broker-side user can belong to several
  // brokers, but tenant_id pins exactly one of them and switching does not
  // change it, so this used to resolve to the wrong broker and the payout came
  // back empty or 403 (QA point 17). Fall back to tenant_id when there is no
  // broker context (single-broker users, non-broker logins).
  const selectedBrokerId =
    loginType === 'broker' ? (tenantContext as BrokerContext | null)?.id ?? undefined : undefined;
  const brokerId = selectedBrokerId ?? user?.tenant_id ?? undefined;

  // Data hooks
  const ratesQuery = useBrokerRates(ratesPage, rateFilters);
  const calcsFilters = calcStatusFilter ? { status: calcStatusFilter } : undefined;
  const calcsQuery = useCommissionCalculations(calcsPage, calcsFilters);
  const payoutsQuery = useCommissionPayouts(payoutsPage);
  const balanceQuery = useRunningBalance(brokerId);
  const pospLookupQuery = usePOSPsLookup();
  const outstandingQuery = usePospOutstanding();

  // Mutations
  const approveCalc = useApproveCalculation();
  const disputeCalc = useDisputeCalculation();
  const createPayout = useCreatePayoutBatches();
  const markProcessing = useMarkPayoutProcessing();
  const markCompleted = useMarkPayoutCompleted();

  // Create Payout modal (item 7 step 1) — pick who to pay (broker/self or one or
  // more POSPs) + period, then POST create_batch once per selected entity.
  const [createPayoutOpen, setCreatePayoutOpen] = useState(false);
  const [prefillPospIds, setPrefillPospIds] = useState<string[]>([]);

  const openCreatePayout = (pospId?: string) => {
    setPrefillPospIds(pospId ? [pospId] : []);
    setCreatePayoutOpen(true);
  };

  const handleSubmitCreatePayout = (payload: CreatePayoutBatchesInput) => {
    createPayout.mutate(payload, {
      // Close only when nothing failed; otherwise the modal stays open with the
      // same selection so the user can narrow the period and retry.
      onSuccess: ({ failed }) => {
        if (failed.length === 0) setCreatePayoutOpen(false);
      },
    });
  };

  // Complete Payout modal (item 7 step 2) — capture payment_reference/date/mode
  // before marking a payout completed, instead of silently sending an empty
  // payment_reference the way the old one-click handler did.
  const [completingPayout, setCompletingPayout] = useState<CommissionPayoutRecord | null>(null);

  const handleSubmitCompletePayout = (payment: {
    paymentReference: string;
    paymentDate: string;
    paymentMode: string;
  }) => {
    if (!completingPayout) return;
    markCompleted.mutate(
      { id: completingPayout.id, ...payment },
      { onSuccess: () => setCompletingPayout(null) },
    );
  };

  return (
    <div>
      <PageHeader
        title="Commissions"
        subtitle="Manage commission rates, calculations, ledger, and payouts"
        breadcrumbs={[{ label: 'Commissions' }]}
        actions={
          // #428: only the broker admin imports grids (D13); the route and API enforce it too.
          // #434: broker admin, country admin and super admin open the Grids pages.
          ['broker_admin', 'country_admin', 'super_admin'].includes(user?.role ?? '') ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/commissions/grids')}>
                Grids
              </Button>
              {user?.role === 'broker_admin' && (
                <Button variant="outline" onClick={() => navigate('/commissions/grids/import')}>
                  Import insurer grid
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
        {/* whitespace-nowrap + a scrolling strip: "Commission Reconciliation" is a
            long label and the six tabs no longer fit a phone-width row. */}
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Rates Tab */}
      {activeTab === 'rates' && (
        <RatesTab
          data={ratesQuery.data}
          isLoading={ratesQuery.isLoading}
          page={ratesPage}
          onPageChange={setRatesPage}
          filters={rateFilters}
          onFiltersChange={(next) => {
            // Any filter change moves the result set, so page 3 of the old
            // set is meaningless against the new one.
            setRateFilters(next);
            setRatesPage(1);
          }}
        />
      )}

      {/* Calculations Tab */}
      {activeTab === 'calculations' && (
        <CalculationsTab
          data={calcsQuery.data}
          isLoading={calcsQuery.isLoading}
          page={calcsPage}
          onPageChange={setCalcsPage}
          statusFilter={calcStatusFilter}
          onStatusFilterChange={(v) => { setCalcStatusFilter(v); setCalcsPage(1); }}
          onApprove={(id) => approveCalc.mutate(id)}
          onDispute={(id) => disputeCalc.mutate(id)}
          approving={approveCalc.isPending}
          disputing={disputeCalc.isPending}
        />
      )}

      {/* Commission Reconciliation Tab — the insurer's side of the Calculations
          figures, which is why it sits directly beside them. */}
      {activeTab === 'comparison' && <CommissionReconciliationTab />}

      {activeTab === 'settlements' && <InsurerSettlementsTab />}

      {/* Ledger Tab */}
      {activeTab === 'ledger' && <LedgerTab balance={balanceQuery.data?.balance} />}

      {/* Payouts Tab */}
      {activeTab === 'payouts' && (
        <PayoutsTab
          data={payoutsQuery.data}
          isLoading={payoutsQuery.isLoading}
          page={payoutsPage}
          onPageChange={setPayoutsPage}
          onCreatePayout={() => openCreatePayout()}
          creatingPayout={createPayout.isPending}
          onMarkProcessing={(id) => markProcessing.mutate(id)}
          onMarkCompleted={(payout) => setCompletingPayout(payout)}
          outstanding={outstandingQuery.data}
          outstandingLoading={outstandingQuery.isLoading}
          onCreatePayoutForPosp={openCreatePayout}
        />
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && <ReportsTab />}

      <CreatePayoutModal
        isOpen={createPayoutOpen}
        onClose={() => setCreatePayoutOpen(false)}
        onSubmit={handleSubmitCreatePayout}
        isSubmitting={createPayout.isPending}
        pospOptions={pospLookupQuery.data ?? []}
        initialPospIds={prefillPospIds}
      />

      <CompletePayoutModal
        payout={completingPayout}
        onClose={() => setCompletingPayout(null)}
        onSubmit={handleSubmitCompletePayout}
        isSubmitting={markCompleted.isPending}
      />
    </div>
  );
}

// ============================================================
// Rates Tab
// ============================================================
function RatesTab({
  data,
  isLoading,
  page,
  onPageChange,
  filters,
  onFiltersChange,
}: {
  data: ReturnType<typeof useBrokerRates>['data'];
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  filters: RateDateFilters;
  onFiltersChange: (next: RateDateFilters) => void;
}) {
  const rates = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20) || 1;
  const hasFilters = Object.values(filters).some(Boolean);
  const set = (patch: Partial<RateDateFilters>) => onFiltersChange({ ...filters, ...patch });

  return (
    <>
      {/* #249 — custom date filters for Effective From and Effective To. */}
      <Card className="mb-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Input
              id="rate-filter-effective-from-after"
              label="Effective From — on or after"
              type="date"
              value={filters.effective_from_after}
              onChange={(e) => set({ effective_from_after: e.target.value })}
            />
            <Input
              id="rate-filter-effective-from-before"
              label="Effective From — on or before"
              type="date"
              value={filters.effective_from_before}
              onChange={(e) => set({ effective_from_before: e.target.value })}
            />
            <Input
              id="rate-filter-effective-until-after"
              label="Effective To — on or after"
              type="date"
              value={filters.effective_until_after}
              onChange={(e) => set({ effective_until_after: e.target.value })}
            />
            <Input
              id="rate-filter-effective-until-before"
              label="Effective To — on or before"
              type="date"
              value={filters.effective_until_before}
              onChange={(e) => set({ effective_until_before: e.target.value })}
            />
          </div>
          {hasFilters && (
            <Button variant="secondary" onClick={() => onFiltersChange(emptyRateFilters)}>
              Clear dates
            </Button>
          )}
        </div>
        {/* Said out loud because it is the one rule nobody guesses, and a rate
            that vanished from a filtered list is a rate nobody reconciles. */}
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          A rate with no Effective To never expires: it is included by &ldquo;on or after&rdquo;
          and excluded by &ldquo;on or before&rdquo;.
        </p>
      </Card>

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton cols={8} />
        ) : rates.length === 0 ? (
          <EmptyState
            message={
              hasFilters
                ? 'No commission rates match these dates.'
                : 'No commission rates configured yet.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {/* "Effective To" is new (#249): the tab grew a filter on a
                        column it did not show, and a filter you cannot check
                        against the row is a filter nobody trusts. */}
                    {['Insurer', 'Product / Type', 'Percentage', 'Flat', 'Premium Base', 'Effective From', 'Effective To', 'Status'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rates.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                        {/* U-05: the insurer's logo beside its name. */}
                        <span className="flex items-center gap-2">
                          <InsurerLogo src={r.insurer_logo_url} name={r.insurer_name} size="xs" />
                          <EntityLink kind="insurer" id={r.insurer} name={r.insurer_name} />
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                        {r.product_name || r.insurance_type_name || '-'}
                        {/* Item 10. The same grid is reachable from two places,
                            and a customer-scoped rate is only identifiable by
                            the contact it names: 25% and 10% on the same insurer
                            and type, side by side, with nothing saying which is
                            whose. Mirrors BrokerInsurerCommissionsPage. */}
                        {r.customer_name && (
                          <span className="mt-1 block text-xs font-medium text-indigo-700 dark:text-indigo-300">
                            Customer: {r.customer_name}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{r.commission_percentage}%</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{r.commission_flat || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{titleCase(r.premium_base)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(r.effective_from)}</td>
                      {/* Not "—": the blank is the whole reason the two
                          Effective To filters are asymmetric. */}
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {r.effective_until ? formatDate(r.effective_until) : 'No end date'}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={r.is_active ? 'success' : 'default'} size="sm">
                          {r.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} itemsPerPage={20} onPageChange={onPageChange} />
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ============================================================
// Calculations Tab
// ============================================================
function CalculationsTab({
  data,
  isLoading,
  page,
  onPageChange,
  statusFilter,
  onStatusFilterChange,
  onApprove,
  onDispute,
  approving,
  disputing,
}: {
  data: ReturnType<typeof useCommissionCalculations>['data'];
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  statusFilter: string;
  onStatusFilterChange: (v: string) => void;
  onApprove: (id: string) => void;
  onDispute: (id: string) => void;
  approving: boolean;
  disputing: boolean;
}) {
  const calcs = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20) || 1;
  // B4: approve and calculate move money, so they are ADMIN_ONLY on the server
  // (an insurer admin can open this screen and would only get a 403); dispute
  // is commissions Read on the server -- Wave 2 kept it open to broker staff
  // and to a POSP disputing their own calculation, and commissions.edit is
  // POSP_LOCKED, so gating on edit here would hide the button for every POSP.
  const role = useAuthStore((s) => s.user?.role);
  const isMoneyAdmin = !!role && ADMIN_ROLES.includes(role);
  // `useCanState`, not `useCan`: a FAILED rights request reads as false in
  // every cell, so a 502 during a gunicorn reload would remove Dispute from
  // every row with nothing on screen to tell that apart from a revoked right.
  // It still fails closed; the notice below just says which it is.
  const {
    allowed: canDisputeRight,
    isError: disputeRightUnknown,
    retry: retryDisputeRight,
  } = useCanState('commissions', 'read');

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const calculate = useCalculateCommissions();
  // Queue #200/#221: the id of the background run this page is following.
  const [runId, setRunId] = useState<string | null>(null);
  const { data: run } = useCommissionCalcRun(runId);
  const isRunning = Boolean(run && run.status !== 'completed' && run.status !== 'failed');

  return (
    <>
      <Card className="mb-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-xs">
            <Select
              label="Status"
              options={calcStatusOptions}
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <Input
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <Input
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
            {isMoneyAdmin && (
              <Button
                onClick={() =>
                  calculate.mutate(
                    { fromDate, toDate },
                    { onSuccess: (started) => setRunId(started.id) }
                  )
                }
                isLoading={calculate.isPending || isRunning}
                disabled={calculate.isPending || isRunning}
              >
                Calculate Commission
              </Button>
            )}
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Calculates commissions for issued policies in the date range that have a valid
          commission rate and haven&apos;t been calculated yet.
        </p>

        {/* Queue #200/#221: the run happens in the background now, so the page
            has to show where it is. Without this the button would simply go
            quiet for minutes -- which is indistinguishable from the timeout
            being replaced. */}
        {run && isRunning && (
          <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20">
            <div className="flex items-center justify-between text-sm text-primary-800 dark:text-primary-200">
              <span>{formatCalcRunProgress(run)}</span>
              <span className="font-semibold">{run.percent}%</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-primary-100 dark:bg-primary-900">
              <div
                className="h-full rounded-full bg-primary-600 transition-all duration-500"
                style={{ width: `${run.percent}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-primary-700 dark:text-primary-300">
              This runs in the background — you can leave this page and come back.
            </p>
          </div>
        )}
      </Card>

      {disputeRightUnknown && (
        <p role="alert" className="mb-4 text-sm text-amber-700 dark:text-amber-500">
          We could not check your commissions access, so Dispute is hidden. This is a connection
          problem, not a refusal.{' '}
          {retryDisputeRight && (
            <button type="button" onClick={() => retryDisputeRight()} className="underline">
              Try again
            </button>
          )}
        </p>
      )}

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton cols={7} />
        ) : calcs.length === 0 ? (
          <EmptyState message="No commission calculations found." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {['Policy', 'Date', 'Product', 'Broker Comm.', 'POSP Comm.', 'Total', 'Status', 'Actions'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {calcs.map((c) => {
                    const canApprove = isMoneyAdmin && c.status === 'calculated';
                    const canDispute = canDisputeRight && (c.status === 'calculated' || c.status === 'approved');
                    return (
                      <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        {/* Global #7: an em dash, never `policy_reference`.
                            That field is OUR internal reference and is no longer
                            served at all (CommissionCalculationListSerializer);
                            this screen is open to insurer admins too.
                            B-05: our number, when it is all there is, is tagged. */}
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          <PolicyNumber source={collapsedPolicyNumberSource(c.policy_number, c.insurer_policy_number)} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(c.policy_issue_date || c.policy_date)}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{c.product_name}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(c.broker_commission)}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(c.posp_commission)}</td>
                        {/* W0-A decision 7: the parts are printed under the total so the sum is visible. */}
                        <td className="px-6 py-4 text-sm"><CommissionTotalCell calc={c} /></td>
                        <td className="px-6 py-4">
                          <Badge variant={getCalcStatusVariant(c.status)} size="sm">{titleCase(c.status)}</Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {canApprove && (
                              <Button size="sm" variant="primary" onClick={() => onApprove(c.id)} disabled={approving}>
                                Approve
                              </Button>
                            )}
                            {canDispute && (
                              <Button size="sm" variant="danger" onClick={() => onDispute(c.id)} disabled={disputing}>
                                Dispute
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} itemsPerPage={20} onPageChange={onPageChange} />
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ============================================================
// Ledger Tab
// ============================================================
function LedgerTab({ balance }: { balance: string | undefined }) {
  const navigate = useNavigate();
  const [grouping, setGrouping] = useState<LedgerGrouping>('chronological');
  // B8 — the two grouped-view filters. They hold an **id**, not typed text.
  //
  // The By-Insurer box used to be a free-text input whose value went straight
  // into the server's `insurer` param, which is read as a UUID or the insurer
  // CODE and nothing else (CommissionReportsViewSet._apply_insurer_filter), so
  // typing a name matched nothing at all. The By-POSP box filtered the returned
  // rows in the browser and hid the pagination control while it was set. Both
  // are dropdowns of ids now, filtered server-side.
  const [groupInsurerId, setGroupInsurerId] = useState('');
  const [groupPospId, setGroupPospId] = useState('');
  const [insurerPage, setInsurerPage] = useState(1);
  const [pospPage, setPospPage] = useState(1);

  // Chronological (individual records) server-side filters.
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [insurerId, setInsurerId] = useState('');
  const [pospId, setPospId] = useState('');
  const [downloading, setDownloading] = useState(false);

  const ledgerFilters = useMemo(() => {
    const f: Record<string, string> = {};
    if (dateFrom) f.date_from = dateFrom;
    if (dateTo) f.date_to = dateTo;
    if (insurerId) f.insurer = insurerId;
    if (pospId) f.posp = pospId;
    return f;
  }, [dateFrom, dateTo, insurerId, pospId]);

  const ledgerQuery = useCommissionLedger(page, ledgerFilters);

  // Dropdown option sources for every Insurer / POSP filter on this tab — one
  // source each, shared by the chronological view and the two groupings, so the
  // same agent or insurer cannot be offered in one and missing from another.
  //
  // The insurer list follows the BrokerSwitcher (see the import comment): a
  // multi-broker user filtering the ledger of broker B must be offered B's
  // insurers, not the ones hanging off the tenant_id that pins broker A.
  const brokerLoginType = useAuthStore((s) => s.loginType);
  const brokerTenantContext = useAuthStore((s) => s.tenantContext);
  const ledgerUser = useAuthStore((s) => s.user);
  // The tenant_id fallback is gated on tenant_type === 'broker' because that is
  // the server's own rule for which ids a caller may name (`_switchable_broker_ids`,
  // apps/brokers/views.py). Naming any other id earns a 403 rather than a list,
  // so a country_admin — who is allowed on this endpoint but whose tenant is not
  // a broker — sends nothing and keeps the resolve-from-membership behaviour.
  const ledgerBrokerId =
    (brokerLoginType === 'broker'
      ? (brokerTenantContext as BrokerContext | null)?.id ?? undefined
      : undefined) ??
    (ledgerUser?.tenant_type === 'broker' ? ledgerUser?.tenant_id ?? undefined : undefined);
  const { data: insurerOptions } = useBrokerInsurers(ledgerBrokerId);
  // U-04: one complete agent list feeds both POSP pickers. usePOSPsLookup()
  // pages through EVERY active agent now, so neither select needs a search box
  // to reach agent 21 (the boxes existed only to work around the old
  // page-one-of-20 cap).
  const { data: pospOptions } = usePOSPsLookup();

  // Always call hooks (rules of hooks); data is only consumed when the relevant
  // grouping is active. Both params on both reports are server-side filters —
  // /commissions/reports/by-posp/ gained `posp` and `insurer` alongside this
  // change, so the By-POSP grouping no longer filters rows in the browser.
  const byInsurerQuery = useByInsurerReport(insurerPage, { insurer: groupInsurerId || undefined });
  const byPospQuery = useByPospLedgerReport(pospPage, {
    insurer: groupInsurerId || undefined,
    posp: groupPospId || undefined,
  });

  // Chronological data
  const entries = ledgerQuery.data?.results ?? [];
  const isLoading = ledgerQuery.isLoading;
  const ledgerFailed = ledgerQuery.isError;
  const totalItems = ledgerQuery.data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20) || 1;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await commissionService.downloadLedger(ledgerFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'commission_ledger.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download ledger');
    } finally {
      setDownloading(false);
    }
  };

  const resetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setInsurerId('');
    setPospId('');
    setPage(1);
  };

  const insurerSelectOptions = [
    { value: '', label: 'All Insurers' },
    ...(insurerOptions ?? []).map((i) => ({ value: i.id, label: i.name })),
  ];
  const pospChoices = (pospOptions ?? []).map((p) => ({
    value: p.id,
    label: p.user_name || p.posp_code,
  }));
  const pospSelectOptions = [{ value: '', label: 'All POSPs' }, ...pospChoices];
  const hasActiveFilters = Boolean(dateFrom || dateTo || insurerId || pospId);
  const filterInputClass =
    'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500';

  const clearGroupFilters = () => {
    setGroupInsurerId('');
    setGroupPospId('');
    setInsurerPage(1);
    setPospPage(1);
  };
  const hasGroupFilters = Boolean(groupInsurerId || groupPospId);

  // By-Insurer data
  const byInsurerRows = byInsurerQuery.data?.results ?? [];
  const byInsurerTotalItems = byInsurerQuery.data?.count ?? 0;
  const byInsurerTotalPages = Math.ceil(byInsurerTotalItems / 20) || 1;

  // By-POSP data — filtered by the server now, so these rows ARE the answer.
  const pospRows = byPospQuery.data?.results ?? [];
  const byPospTotalItems = byPospQuery.data?.count ?? 0;
  const byPospTotalPages = Math.ceil(byPospTotalItems / 20) || 1;

  // The two grouping reports are single-page BY CONTRACT: the server aggregates
  // the whole (already scoped and filtered) set and answers with next/previous
  // null (_page_envelope, apps/commissions/views.py). A pager drawn from
  // count/20 therefore offered pages that re-fetched the identical rows. These
  // follow the server's own next/previous instead, so the control appears only
  // if the endpoint ever really paginates.
  const byInsurerHasPages = Boolean(byInsurerQuery.data?.next || byInsurerQuery.data?.previous);
  const byPospHasPages = Boolean(byPospQuery.data?.next || byPospQuery.data?.previous);

  const groupingLabels: Record<LedgerGrouping, string> = {
    chronological: 'Chronological',
    'by-insurer': 'By Insurer',
    'by-posp': 'By POSP',
  };

  return (
    <>
      {/* Running balance banner */}
      <Card className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Running Balance</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {balance ? formatCurrency(balance) : '--'}
            </p>
          </div>
        </div>
      </Card>

      {/* Grouping toggle + filters */}
      <Card className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Group by
            </p>
            <div className="flex gap-2">
              {(['chronological', 'by-insurer', 'by-posp'] as LedgerGrouping[]).map((g) => (
                <button
                  key={g}
                  onClick={() => setGrouping(g)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    grouping === g
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {groupingLabels[g]}
                </button>
              ))}
            </div>
          </div>

          {grouping !== 'chronological' && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="w-48">
                <label
                  htmlFor="ledger-group-insurer"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Insurer
                </label>
                <Select
                  id="ledger-group-insurer"
                  aria-label="Insurer"
                  options={insurerSelectOptions}
                  value={groupInsurerId}
                  onChange={(e) => {
                    setGroupInsurerId(e.target.value);
                    setInsurerPage(1);
                    setPospPage(1);
                  }}
                />
              </div>
              {grouping === 'by-posp' && (
                <div className="w-48">
                  <label
                    htmlFor="ledger-group-posp"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    POSP
                  </label>
                  <Select
                    id="ledger-group-posp"
                    aria-label="POSP"
                    options={pospSelectOptions}
                    value={groupPospId}
                    onChange={(e) => {
                      setGroupPospId(e.target.value);
                      setPospPage(1);
                    }}
                  />
                </div>
              )}
              {hasGroupFilters && (
                <Button variant="ghost" onClick={clearGroupFilters}>Clear</Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Chronological view */}
      {grouping === 'chronological' && (
        <>
          {/* Date / Insurer / POSP filters + download */}
          <Card className="mb-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                    className={filterInputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                    className={filterInputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Insurer</label>
                  <Select
                    options={insurerSelectOptions}
                    value={insurerId}
                    onChange={(e) => { setInsurerId(e.target.value); setPage(1); }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="ledger-chrono-posp"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    POSP
                  </label>
                  <Select
                    id="ledger-chrono-posp"
                    aria-label="POSP"
                    options={pospSelectOptions}
                    value={pospId}
                    onChange={(e) => {
                      setPospId(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                {hasActiveFilters && (
                  <Button variant="ghost" onClick={resetFilters}>Clear</Button>
                )}
                <Button variant="outline" onClick={handleDownload} isLoading={downloading}>
                  Download CSV
                </Button>
              </div>
            </div>
          </Card>

          <Card padding="none">
            {isLoading ? (
              <TableSkeleton cols={8} />
            ) : ledgerFailed ? (
              <LoadFailed
                what="this ledger"
                error={ledgerQuery.error}
                onRetry={() => ledgerQuery.refetch()}
              />
            ) : entries.length === 0 ? (
              <EmptyState message="No ledger entries found." />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        {/* Queue #222: "Account" is the column whose absence made
                            one policy's broker row and POSP row read as duplicate
                            records -- they shared policy number, date, type and
                            description, and differed only in amount. */}
                        {['Date', 'Policy Number', 'Account', 'Insurer', 'Type', 'Amount', 'Balance After', 'Description'].map((h) => (
                          <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {entries.map((e) => {
                        const clickable = Boolean(e.policy_id);
                        return (
                          <tr
                            key={e.id}
                            onClick={clickable ? () => navigate(`/policies/${e.policy_id}`) : undefined}
                            className={`transition-colors ${
                              clickable
                                ? 'cursor-pointer hover:bg-primary-50 dark:hover:bg-gray-800'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                          >
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(e.transaction_date)}</td>
                            {/* Global #7: the ledger's own fallback to the
                                internal reference is gone from the serializer,
                                and a missing number reads as an em dash here
                                exactly as it does in the Calculations table. */}
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                              <PolicyNumber source={collapsedPolicyNumberSource(e.policy_number, e.insurer_policy_number)} />
                            </td>
                            <td className="px-6 py-4 text-sm">
                              <span className="text-gray-900 dark:text-gray-100">{e.account_name || '-'}</span>
                              <span className="block text-xs text-gray-500 dark:text-gray-400">
                                {titleCase(e.account_type || '')}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                              {/* U-05: logo only where a row names an insurer. */}
                              {e.insurer_name ? (
                                <span className="flex items-center gap-2">
                                  <InsurerLogo src={e.insurer_logo_url} name={e.insurer_name} size="xs" />
                                  <span>{e.insurer_name}</span>
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <Badge variant={entryTypeBadgeVariant[e.entry_type] || 'default'} size="sm">
                                {titleCase(e.entry_type)}
                              </Badge>
                            </td>
                            <td className={`px-6 py-4 text-sm font-medium ${e.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {e.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(e.amount)}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(e.balance_after)}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                              {e.description}
                              {/* #280. The reason a clawback was posted. Without
                                  it a negative agent balance is unexplained
                                  everywhere a user can reach. */}
                              {e.note ? (
                                <span className="block text-xs italic text-gray-400 dark:text-gray-500">{e.note}</span>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} itemsPerPage={20} onPageChange={setPage} />
                )}
              </>
            )}
          </Card>
        </>
      )}

      {/* By Insurer view */}
      {grouping === 'by-insurer' && (
        <Card padding="none">
          {byInsurerQuery.isLoading ? (
            <TableSkeleton cols={6} />
          ) : byInsurerQuery.isError ? (
            <LoadFailed
              what="the insurer breakdown"
              error={byInsurerQuery.error}
              onRetry={() => byInsurerQuery.refetch()}
            />
          ) : byInsurerRows.length === 0 ? (
            <EmptyState
              message={
                groupInsurerId
                  ? 'No ledger activity for that insurer in this period.'
                  : 'No insurer data found.'
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      {['Insurer', 'Received', 'Given', 'Margin', 'Policies', 'Premium'].map((h) => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {byInsurerRows.map((row) => (
                      <tr key={row.id || row.insurer_code} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {/* U-05: no logo for the "Direct / Unknown" bucket, which is no company. */}
                          {row.id ? (
                            <span className="flex items-center gap-2">
                              <InsurerLogo src={row.insurer_logo_url} name={row.insurer_name} size="xs" />
                              <span>{row.insurer_name}</span>
                            </span>
                          ) : (
                            row.insurer_name
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400 font-medium">{formatCurrency(row.received)}</td>
                        <td className="px-6 py-4 text-sm text-orange-600 dark:text-orange-400 font-medium">{formatCurrency(row.given)}</td>
                        <td className="px-6 py-4 text-sm text-blue-600 dark:text-blue-400 font-medium">{formatCurrency(row.margin)}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{row.policy_count}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{formatCurrency(row.premium)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {byInsurerHasPages && (
                <Pagination currentPage={insurerPage} totalPages={byInsurerTotalPages} totalItems={byInsurerTotalItems} itemsPerPage={20} onPageChange={setInsurerPage} />
              )}
            </>
          )}
        </Card>
      )}

      {/* By POSP view */}
      {grouping === 'by-posp' && (
        <Card padding="none">
          {byPospQuery.isLoading ? (
            <TableSkeleton cols={6} />
          ) : byPospQuery.isError ? (
            <LoadFailed
              what="the agent breakdown"
              error={byPospQuery.error}
              onRetry={() => byPospQuery.refetch()}
            />
          ) : pospRows.length === 0 ? (
            <EmptyState
              message={
                hasGroupFilters
                  ? 'No agent activity matches those filters in this period.'
                  : 'No POSP data found.'
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      {['Agent (POSP)', 'Earnings', 'Policies', 'Paid', 'Pending', 'Status'].map((h) => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {pospRows.map((row) => (
                      <tr key={row.id || row.posp_code} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{row.posp_name}</td>
                        <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400 font-medium">{formatCurrency(row.earnings)}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{row.policy_count}</td>
                        <td className="px-6 py-4 text-sm text-blue-600 dark:text-blue-400 font-medium">{formatCurrency(row.paid)}</td>
                        <td className="px-6 py-4 text-sm text-orange-600 dark:text-orange-400 font-medium">{formatCurrency(row.pending)}</td>
                        <td className="px-6 py-4">
                          <Badge variant={getPospPaymentStatusVariant(row.payment_status)} size="sm">
                            {titleCase(row.payment_status)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* No longer hidden by the filter: the filter is server-side, so
                  the rows and the paging describe the same set. */}
              {byPospHasPages && (
                <Pagination currentPage={pospPage} totalPages={byPospTotalPages} totalItems={byPospTotalItems} itemsPerPage={20} onPageChange={setPospPage} />
              )}
            </>
          )}
        </Card>
      )}
    </>
  );
}

// ============================================================
// Payouts Tab
// ============================================================
function PayoutsTab({
  data,
  isLoading,
  page,
  onPageChange,
  onCreatePayout,
  creatingPayout,
  onMarkProcessing,
  onMarkCompleted,
  outstanding,
  outstandingLoading,
  onCreatePayoutForPosp,
}: {
  data: ReturnType<typeof useCommissionPayouts>['data'];
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  onCreatePayout: () => void;
  creatingPayout: boolean;
  onMarkProcessing: (id: string) => void;
  onMarkCompleted: (payout: CommissionPayoutRecord) => void;
  outstanding: ReturnType<typeof usePospOutstanding>['data'];
  outstandingLoading: boolean;
  onCreatePayoutForPosp: (pospId: string) => void;
}) {
  const payouts = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  // B4: create_batch and mark_* are ADMIN_ONLY on the server.
  const payoutRole = useAuthStore((s) => s.user?.role);
  const canManagePayouts = !!payoutRole && ADMIN_ROLES.includes(payoutRole);
  const totalPages = Math.ceil(totalItems / 20) || 1;
  // B13: the payout whose policies are open in the line view.
  const [viewingPayout, setViewingPayout] = useState<CommissionPayoutWithPosp | null>(null);
  const outstandingRows = outstanding?.results ?? [];
  const outstandingSummary = outstanding?.summary;

  return (
    <>
      {/* Outstanding owed per POSP (item 7 step 3) — approved-but-unpaid commission,
          i.e. what a Create Payout batch would sweep up for that POSP right now. */}
      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Outstanding — Owed to POSPs
        </h3>
        {outstandingLoading ? (
          <TableSkeleton cols={3} />
        ) : outstandingRows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nothing outstanding — every approved commission has been paid out.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  {['Agent (POSP)', 'Policies', 'Owed', ''].map((h) => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {outstandingRows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">{row.posp_name}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{row.policy_count}</td>
                    <td className="px-4 py-2 text-sm text-orange-600 dark:text-orange-400 font-medium">{formatCurrency(row.owed)}</td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => onCreatePayoutForPosp(row.id)}>
                        Pay
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {outstandingSummary && (
                <tfoot>
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Total</td>
                    <td />
                    <td className="px-4 py-2 text-sm font-semibold text-orange-600 dark:text-orange-400">
                      {formatCurrency(outstandingSummary.total_owed)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      {canManagePayouts && (
        <div className="flex justify-end mb-4">
          <Button onClick={onCreatePayout} isLoading={creatingPayout}>
            Create Payout
          </Button>
        </div>
      )}

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton cols={11} />
        ) : payouts.length === 0 ? (
          <EmptyState message="No payout records found." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {['Reference', 'POSP', 'Period', 'Gross', 'TDS', 'GST', 'Net', 'Policies', 'Mode', 'Status', 'Actions'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {payouts.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{p.payout_reference}</td>
                      {/* B13: payouts are POSP-wise — name the agent, not only a code. */}
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                        {p.posp_name || p.posp_code || p.broker_name || '—'}
                        {p.posp_name && p.posp_code && (
                          <span className="block text-xs text-gray-500 dark:text-gray-400">{p.posp_code}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(p.period_start)} - {formatDate(p.period_end)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{formatCurrency(p.gross_amount)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatCurrency(p.tds_amount)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatCurrency(p.gst_amount)}</td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-gray-100">{formatCurrency(p.net_amount)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{p.policy_count}</td>
                      {/* #253: how this payout was paid. The mode was already on the
                          API payload and rendered nowhere, so a user who chose
                          "By Cash" had no way to confirm the choice had stuck. */}
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{paymentModeLabel(p.payment_mode)}</td>
                      <td className="px-6 py-4">
                        <Badge variant={getStatusBadgeVariant(p.status)} size="sm">{titleCase(p.status)}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setViewingPayout(p)}>
                            View Policies
                          </Button>
                          {canManagePayouts && p.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => onMarkProcessing(p.id)}>
                              Process
                            </Button>
                          )}
                          {canManagePayouts && p.status === 'processing' && (
                            <Button size="sm" variant="primary" onClick={() => onMarkCompleted(p)}>
                              Complete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} itemsPerPage={20} onPageChange={onPageChange} />
            )}
          </>
        )}
      </Card>

      <PayoutLinesModal payout={viewingPayout} onClose={() => setViewingPayout(null)} />
    </>
  );
}

// ============================================================
// Create Payout Modal (item 7 step 1)
// ============================================================
/**
 * Records a payout to one or more POSPs. **Paying "Myself (Broker)" was removed
 * here (B9)** — the payee selector is gone and every batch this modal creates
 * carries `entity_type: "posp"`.
 *
 * CREATION ONLY. Four `entity_type="broker"` payouts exist in production and two
 * are still `processing`, holding Rs 19,763. They are completed through
 * `mark_completed` -> `PayoutProcessor.process_payout`, whose broker branch
 * writes their ledger rows, and they are listed by a `PayoutsTab` that never
 * reads `entity_type`. Nothing on that path was touched, so those rows keep
 * rendering and can still be settled; see
 * `backend/tests/test_broker_self_payout_completion.py`, which exists to fail
 * loudly if a later change closes the broker branch and strands that money.
 *
 * One behavioural side effect, deliberately left alone: `create_payout` flips
 * the calculations it sweeps to `status="paid"`, so a broker's OWN approved
 * `broker_commission` rows are no longer flipped by this path.
 */
export function CreatePayoutModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  pospOptions,
  initialPospIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePayoutBatchesInput) => void;
  isSubmitting: boolean;
  pospOptions: { id: string; user_name: string; posp_code: string }[];
  /** Pre-ticked POSPs (the Outstanding table opens the modal with one). */
  initialPospIds: string[];
}) {
  const [pospIds, setPospIds] = useState<string[]>([]);
  const [pospQuery, setPospQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Re-seed the form defaults every time the modal is (re)opened, including
  // opening it pre-filled for a specific POSP from the Outstanding table.
  useEffect(() => {
    if (!isOpen) return;
    const { start, end } = defaultPayoutRange();
    setPospIds(initialPospIds);
    setPospQuery('');
    setStartDate(start);
    setEndDate(end);
  }, [isOpen, initialPospIds]);

  const pospChoices = useMemo(
    () => pospOptions.map((p) => ({ id: p.id, label: p.user_name || p.posp_code })),
    [pospOptions],
  );

  const visibleChoices = useMemo(() => {
    const q = pospQuery.trim().toLowerCase();
    if (!q) return pospChoices;
    return pospChoices.filter((p) => p.label.toLowerCase().includes(q));
  }, [pospChoices, pospQuery]);

  const togglePosp = (id: string) =>
    setPospIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Select-all acts on what the search box is currently showing, never on the
  // hidden remainder — ticking 4 visible names must not silently queue 40.
  const allVisibleSelected =
    visibleChoices.length > 0 && visibleChoices.every((p) => pospIds.includes(p.id));

  const toggleAllVisible = () =>
    setPospIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleChoices.some((p) => p.id === id))
        : Array.from(new Set([...prev, ...visibleChoices.map((p) => p.id)])),
    );

  const canSubmit = Boolean(startDate) && Boolean(endDate) && pospIds.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const entities = pospIds.map((id) => ({
      id,
      label: pospChoices.find((p) => p.id === id)?.label ?? id,
    }));
    onSubmit({ entity_type: 'posp', entities, start_date: startDate, end_date: endDate });
  };

  const payoutCount = pospIds.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title="Create Payout"
      description="Sweep approved commissions for a period into a payout batch."
    >
      <div className="space-y-4">
        {/* B9: the "Pay: Myself (Broker) | POSPs" selector was removed. A payout
            created here is always to POSPs. */}
        <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                POSPs
                {pospIds.length > 0 && (
                  <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                    ({pospIds.length} selected)
                  </span>
                )}
              </label>
              {visibleChoices.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllVisible}
                  className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {allVisibleSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>
            <input
              type="search"
              value={pospQuery}
              onChange={(e) => setPospQuery(e.target.value)}
              placeholder="Search POSPs…"
              className={dateInputClass}
            />
            <div
              role="group"
              aria-label="POSPs to pay"
              className="mt-2 max-h-48 overflow-y-auto rounded-md border border-gray-300 dark:border-gray-600 divide-y divide-gray-100 dark:divide-gray-700"
            >
              {pospChoices.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No active POSPs to pay.
                </p>
              ) : visibleChoices.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No POSP matches that search.
                </p>
              ) : (
                visibleChoices.map((p) => (
                  <label
                    key={p.id}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={pospIds.includes(p.id)}
                      onChange={() => togglePosp(p.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-gray-900 dark:text-gray-100">{p.label}</span>
                  </label>
                ))
              )}
            </div>
            {/* Says out loud what the code guarantees: a payout row carries one
                POSP, so a POSP can never see a peer's payout or bank details. */}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            A separate payout is created for each POSP — every POSP sees only their own.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Period Start</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={dateInputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Period End</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={dateInputClass} />
          </div>
        </div>
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={!canSubmit}>
          {payoutCount > 1 ? `Create ${payoutCount} Payouts` : 'Create Payout'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ============================================================
// Complete Payout Modal (item 7 step 2)
// ============================================================
export function CompletePayoutModal({
  payout,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  payout: CommissionPayoutRecord | null;
  onClose: () => void;
  onSubmit: (payment: { paymentReference: string; paymentDate: string; paymentMode: string }) => void;
  isSubmitting: boolean;
}) {
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentMode, setPaymentMode] = useState('bank_transfer');

  useEffect(() => {
    if (!payout) return;
    setPaymentReference('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMode('bank_transfer');
  }, [payout]);

  const handleSubmit = () => {
    if (!paymentReference.trim()) {
      toast.error('Payment reference is required');
      return;
    }
    onSubmit({ paymentReference: paymentReference.trim(), paymentDate, paymentMode });
  };

  return (
    <Modal
      isOpen={Boolean(payout)}
      onClose={onClose}
      title="Complete Payout"
      description={payout ? `${payout.payout_reference} — ${formatCurrency(payout.net_amount)}` : undefined}
    >
      <div className="space-y-4">
        <Input
          label="Payment Reference"
          placeholder={paymentReferencePlaceholder(paymentMode)}
          value={paymentReference}
          onChange={(e) => setPaymentReference(e.target.value)}
          required
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Date</label>
          <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className={dateInputClass} />
        </div>
        <Select
          label="Payment Mode"
          options={PAYMENT_MODE_OPTIONS}
          value={paymentMode}
          onChange={(e) => setPaymentMode(e.target.value)}
        />
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} isLoading={isSubmitting}>Mark Completed</Button>
      </ModalFooter>
    </Modal>
  );
}

// ============================================================
// Helpers
// ============================================================
function getPospPaymentStatusVariant(
  status: 'fully_paid' | 'partially_paid' | 'pending',
): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'fully_paid': return 'success';
    case 'partially_paid': return 'warning';
    default: return 'default';
  }
}

function getCalcStatusVariant(status: CommissionCalculationStatus) {
  const map: Record<CommissionCalculationStatus, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    calculated: 'info',
    approved: 'success',
    paid: 'success',
    disputed: 'warning',
    cancelled: 'error',
  };
  return map[status] || 'default';
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-100 dark:bg-gray-800" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-16 border-t border-gray-100 dark:border-gray-700 flex items-center px-6 gap-4">
          {[...Array(cols)].map((_, j) => (
            <div key={j} className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * The THIRD state every list on this page needs.
 *
 * `isLoading ? skeleton : rows.length === 0 ? <EmptyState/> : table` has no
 * branch for a request that failed, and TanStack Query v5 only substitutes
 * `placeholderData` while `status === "pending"` — so a 500, a dropped
 * connection or an expired session lands on the empty state and the page says
 * "this broker has no commission" when the truth is "we could not load it".
 * That is a claim about someone's money made out of a network error.
 *
 * It is also recoverable, so it offers the retry rather than making the user
 * reload the whole page and lose their filters.
 */
function LoadFailed({
  what,
  error,
  onRetry,
}: {
  what: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="text-center py-12" role="alert">
      <div className="mx-auto h-12 w-12 text-amber-500">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
        Could not load {what}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{getErrorMessage(error)}</p>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        This is a failed request — it does not mean there is nothing here.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-6 text-sm font-medium text-primary-600 hover:text-primary-500"
      >
        Try again
      </button>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">{message}</h3>
    </div>
  );
}
